import * as React from 'react';
import { useState, useEffect } from 'react';
import { Table, Input, Button, Tag, Space, Image, Modal, Typography, Badge, Select, Form, TreeSelect } from 'antd';
import ReactJson from 'react-json-view';
import { SearchOutlined, EyeOutlined, EditOutlined, FilterOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import cardsData from '../../mock/cards.json';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

interface CardData {
  id: string;
  name: string;
  screenshot: string;
  dsl: any;
  status: string;
  tags: string[];
  datasets: string[];
  createTime: string;
  updateTime: string;
}

// 组件类型映射
const componentTypeMap: Record<string, { category: string; name: string }> = {
  // 布局组件
  list: { category: '布局组件', name: '列表' },
  flex: { category: '布局组件', name: '弹性布局' },
  grid: { category: '布局组件', name: '网格' },
  collapse: { category: '布局组件', name: '折叠面板' },
  
  // 展示组件
  text: { category: '展示组件', name: '文本' },
  image: { category: '展示组件', name: '图片' },
  divider: { category: '展示组件', name: '分割线' },
  icon: { category: '展示组件', name: '图标' },
  chart: { category: '展示组件', name: '图表' },
  audio: { category: '展示组件', name: '音频' },
  video: { category: '展示组件', name: '视频' },
  progress: { category: '展示组件', name: '进度条' },
  
  // 交互组件
  button: { category: '交互组件', name: '按钮' },
  input: { category: '交互组件', name: '文本输入框' },
  number: { category: '交互组件', name: '数字输入框' },
  select: { category: '交互组件', name: '下拉单选' },
  cascader: { category: '交互组件', name: '级联选择' },
  radio: { category: '交互组件', name: '单选列表' },
  checkbox: { category: '交互组件', name: '多选列表' },
  switch: { category: '交互组件', name: '开关' },
  time: { category: '交互组件', name: '时间选择' },
  upload: { category: '交互组件', name: '文件上传' },
};

const CardAnnotation: React.FC = () => {
  const [cards, setCards] = useState<CardData[]>([]);
  const [nameSearch, setNameSearch] = useState('');
  const [datasetSearch, setDatasetSearch] = useState<string[]>([]);
  const [statusSearch, setStatusSearch] = useState<string>('');
  const [tagSearch, setTagSearch] = useState<string[]>([]);
  const [componentSearch, setComponentSearch] = useState<string[]>([]); // 新增组件筛选状态
  const [tokenCountSearch, setTokenCountSearch] = useState<{ operator: string; value1: number | null; value2: number | null }>({
    operator: 'greater',
    value1: null,
    value2: null
  }); // 新增token数筛选状态
  const [jsonModalVisible, setJsonModalVisible] = useState(false);
  const [currentJson, setCurrentJson] = useState<any>(null);
  const [allDatasets, setAllDatasets] = useState<string[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [tagTreeData, setTagTreeData] = useState<any[]>([]);
  const [componentTreeData, setComponentTreeData] = useState<any[]>([]); // 新增组件树形数据
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const navigate = useNavigate();

  // 遍历DSL查找组件类型
  const findComponentsInDSL = (dsl: any, components: Set<string> = new Set()): Set<string> => {
    if (dsl && typeof dsl === 'object') {
      if (dsl.type) {
        components.add(dsl.type);
      }
      
      // 递归遍历子组件
      if (dsl.children && Array.isArray(dsl.children)) {
        dsl.children.forEach((child: any) => {
          findComponentsInDSL(child, components);
        });
      }
      
      // 遍历其他可能的对象属性
      Object.values(dsl).forEach((value: any) => {
        if (typeof value === 'object' && value !== null) {
          findComponentsInDSL(value, components);
        }
      });
    }
    
    return components;
  };

  // 计算DSL的token数量（简化实现）
  const calculateTokenCount = (dsl: any): number => {
    // 这里是一个简化的token计算方法，实际项目中可能需要更复杂的逻辑
    return JSON.stringify(dsl).length;
  };

  useEffect(() => {
    // 从模拟数据加载卡片
    setCards(cardsData.cards);
    
    // 提取所有唯一的数据集名称和标签
    const datasets = new Set<string>();
    const tags = new Set<string>();
    
    cardsData.cards.forEach(card => {
      card.datasets.forEach(dataset => datasets.add(dataset));
      card.tags.forEach(tag => tags.add(tag));
    });
    
    setAllDatasets(Array.from(datasets));
    setAllTags(Array.from(tags));
    
    // 生成标签树形结构数据
    const tagTreeData = [
      {
        title: '设计风格',
        value: 'design-style',
        key: 'design-style',
        children: [
          { title: '现代简约', value: '现代简约', key: '现代简约' },
          { title: '新闻资讯', value: '新闻资讯', key: '新闻资讯' },
          { title: '多媒体', value: '多媒体', key: '多媒体' },
          { title: '表单交互', value: '表单交互', key: '表单交互' },
          { title: '数据图表', value: '数据图表', key: '数据图表' },
        ],
      },
      {
        title: '业务领域',
        value: 'business-domain',
        key: 'business-domain',
        children: [
          { title: '电商', value: '电商', key: '电商' },
          { title: '媒体', value: '媒体', key: '媒体' },
          { title: '娱乐', value: '娱乐', key: '娱乐' },
          { title: '工具', value: '工具', key: '工具' },
          { title: '数据分析', value: '数据分析', key: '数据分析' },
        ],
      },
      {
        title: '功能类型',
        value: 'function-type',
        key: 'function-type',
        children: [
          { title: '商品展示', value: '商品展示', key: '商品展示' },
          { title: '信息展示', value: '信息展示', key: '信息展示' },
          { title: '视频播放', value: '视频播放', key: '视频播放' },
          { title: '用户输入', value: '用户输入', key: '用户输入' },
          { title: '可视化', value: '可视化', key: '可视化' },
        ],
      },
    ];
    
    setTagTreeData(tagTreeData);
    
    // 生成组件树形结构数据
    const componentTreeData = [
      {
        title: '布局组件',
        value: 'layout',
        key: 'layout',
        children: [
          { title: '列表', value: 'list', key: 'list' },
          { title: '弹性布局', value: 'flex', key: 'flex' },
          { title: '网格', value: 'grid', key: 'grid' },
          { title: '折叠面板', value: 'collapse', key: 'collapse' },
        ],
      },
      {
        title: '展示组件',
        value: 'display',
        key: 'display',
        children: [
          { title: '文本', value: 'text', key: 'text' },
          { title: '图片', value: 'image', key: 'image' },
          { title: '分割线', value: 'divider', key: 'divider' },
          { title: '图标', value: 'icon', key: 'icon' },
          { title: '图表', value: 'chart', key: 'chart' },
          { title: '音频', value: 'audio', key: 'audio' },
          { title: '视频', value: 'video', key: 'video' },
          { title: '进度条', value: 'progress', key: 'progress' },
        ],
      },
      {
        title: '交互组件',
        value: 'interactive',
        key: 'interactive',
        children: [
          { title: '按钮', value: 'button', key: 'button' },
          { title: '文本输入框', value: 'input', key: 'input' },
          { title: '数字输入框', value: 'number', key: 'number' },
          { title: '下拉单选', value: 'select', key: 'select' },
          { title: '级联选择', value: 'cascader', key: 'cascader' },
          { title: '单选列表', value: 'radio', key: 'radio' },
          { title: '多选列表', value: 'checkbox', key: 'checkbox' },
          { title: '开关', value: 'switch', key: 'switch' },
          { title: '时间选择', value: 'time', key: 'time' },
          { title: '文件上传', value: 'upload', key: 'upload' },
        ],
      },
    ];
    
    setComponentTreeData(componentTreeData);
  }, []);

  // 处理搜索
  const handleSearch = () => {
    let filteredCards = [...cardsData.cards];
    
    // 按名称筛选
    if (nameSearch) {
      filteredCards = filteredCards.filter(card => 
        card.name.toLowerCase().includes(nameSearch.toLowerCase())
      );
    }
    
    // 按数据集筛选
    if (datasetSearch && datasetSearch.length > 0) {
      filteredCards = filteredCards.filter(card => 
        card.datasets.some(dataset => datasetSearch.includes(dataset))
      );
    }
    
    // 按状态筛选
    if (statusSearch) {
      filteredCards = filteredCards.filter(card => 
        card.status === statusSearch
      );
    }
    
    // 按卡片标签筛选（或条件）
    if (tagSearch && tagSearch.length > 0) {
      filteredCards = filteredCards.filter(card => 
        tagSearch.some(tag => card.tags.includes(tag))
      );
    }
    
    // 按组件类型筛选
    if (componentSearch && componentSearch.length > 0) {
      filteredCards = filteredCards.filter(card => {
        // 查找卡片DSL中包含的组件类型
        const cardComponents = findComponentsInDSL(card.dsl);
        // 检查是否有匹配的组件类型
        return componentSearch.some(comp => cardComponents.has(comp));
      });
    }
    
    // 按token数筛选
    if (tokenCountSearch.value1 !== null) {
      const value1 = tokenCountSearch.value1;
      const value2 = tokenCountSearch.value2;
      
      switch (tokenCountSearch.operator) {
        case 'greater':
          filteredCards = filteredCards.filter(card => 
            calculateTokenCount(card.dsl) > value1
          );
          break;
        case 'less':
          filteredCards = filteredCards.filter(card => 
            calculateTokenCount(card.dsl) < value1
          );
          break;
        case 'between':
          if (value2 !== null) {
            const min = Math.min(value1, value2);
            const max = Math.max(value1, value2);
            filteredCards = filteredCards.filter(card => {
              const tokenCount = calculateTokenCount(card.dsl);
              return tokenCount >= min && tokenCount <= max;
            });
          }
          break;
      }
    }
    
    {/* 删除卡片领域筛选 */}
    
    setCards(filteredCards);
  };

  // 重置搜索条件
  const resetSearch = () => {
    setNameSearch('');
    setDatasetSearch([]);
    setStatusSearch('');
    setTagSearch([]);
    setComponentSearch([]); // 重置组件筛选
    setTokenCountSearch({
      operator: 'greater',
      value1: null,
      value2: null
    }); // 重置token数筛选
    setCards(cardsData.cards);
  };

  // 查看JSON
  const handleViewJson = (record: CardData) => {
    setCurrentJson(record.dsl);
    setJsonModalVisible(true);
  };

  // 前往标注页面
  const handleAnnotate = (record: CardData) => {
    // 在新窗口中打开独立的标注详情页面
    const newWindow = window.open(`/#/annotation-detail-standalone/${record.id}`, '_blank');
    if (newWindow) {
      newWindow.focus();
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '序号',
      key: 'index',
      width: 80,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: '卡片名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: '卡片截图',
      dataIndex: 'screenshot',
      key: 'screenshot',
      width: 120,
      render: (text: string) => (
        <Image
          width={80}
          src={text}
          fallback="data:image/png;base64,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"
        />
      ),
    },
    {
      title: '卡片DSL',
      dataIndex: 'dsl',
      key: 'dsl',
      width: 200,
      render: (_: any, record: CardData) => (
        <Button type="link" onClick={() => handleViewJson(record)}>
          <EyeOutlined /> 查看DSL
        </Button>
      ),
    },
    {
      title: '卡片状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Badge 
          status={status === '已标注' ? 'success' : 'default'} 
          text={status} 
        />
      ),
    },
    {
      title: '卡片标签',
      dataIndex: 'tags',
      key: 'tags',
      width: 200,
      render: (tags: string[]) => (
        <span>
          {tags.map(tag => (
            <Tag color="green" key={tag}>
              {tag}
            </Tag>
          ))}
        </span>
      ),
    },
    {
      title: '包含组件',
      key: 'components',
      width: 250,
      render: (_: any, record: CardData) => {
        // 查找卡片DSL中包含的组件类型
        const cardComponents = Array.from(findComponentsInDSL(record.dsl));
        // 映射组件类型到显示名称
        const componentNames = cardComponents
          .map(type => componentTypeMap[type]?.name || type)
          .filter(name => name); // 过滤掉空值
        
        return (
          <span>
            {componentNames.map((name, index) => (
              <Tag color="blue" key={index}>
                {name}
              </Tag>
            ))}
          </span>
        );
      },
    },
    // 新增token数列
    {
      title: 'token数',
      key: 'tokenCount',
      width: 120,
      render: (_: any, record: CardData) => (
        <span>{calculateTokenCount(record.dsl)}</span>
      ),
    },
    // 新增归属数据集列
    {
      title: '归属数据集',
      dataIndex: 'datasets',
      key: 'datasets',
      width: 200,
      render: (datasets: string[]) => (
        <span>
          {datasets.map(dataset => (
            <Tag color="geekblue" key={dataset}>
              {dataset}
            </Tag>
          ))}
        </span>
      ),
    },
    // 删除创建时间列
    // 删除最后更新时间列
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: CardData) => (
        <Space size="middle">
          <Button 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => handleAnnotate(record)}
          >
            去标注
          </Button>
        </Space>
      ),
    },
  ];

  // 处理分页变化
  const handleTableChange = (pagination: any) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Title level={4}>卡片数据标注</Title>
        </div>
        
        {/* 修改表单布局，使用自定义样式来解决行间距和对齐问题 */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '16px', 
          borderRadius: '4px',
          marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', flex: 1 }}>
            <div>
              <span style={{ marginRight: '8px' }}>卡片名称:</span>
              <Input
                placeholder="搜索卡片名称"
                allowClear
                value={nameSearch}
                onChange={e => setNameSearch(e.target.value)}
                style={{ width: 200 }}
              />
            </div>
            
            <div>
              <span style={{ marginRight: '8px' }}>数据集:</span>
              <Select
                mode="multiple"
                allowClear
                showSearch
                placeholder="选择数据集"
                value={datasetSearch}
                onChange={value => setDatasetSearch(value)}
                style={{ width: 200 }}
                filterOption={(input, option) =>
                  (option?.children as unknown as string).toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {allDatasets.map(dataset => (
                  <Option key={dataset} value={dataset} children={dataset} />
                ))}
              </Select>
            </div>
            
            <div>
              <span style={{ marginRight: '8px' }}>状态:</span>
              <Select
                allowClear
                placeholder="选择状态"
                value={statusSearch || undefined}
                onChange={value => setStatusSearch(value)}
                style={{ width: 150 }}
              >
                <Option value="已标注" children="已标注" />
                <Option value="待标注" children="待标注" />
              </Select>
            </div>
            
            <div>
              <span style={{ marginRight: '8px' }}>卡片标签:</span>
              <TreeSelect
                treeData={tagTreeData}
                placeholder="选择标签"
                value={tagSearch}
                onChange={setTagSearch}
                treeCheckable={true}
                showCheckedStrategy={TreeSelect.SHOW_CHILD}
                allowClear
                style={{ width: 200 }}
              />
            </div>
            
            <div>
              <span style={{ marginRight: '8px' }}>包含组件:</span>
              <TreeSelect
                treeData={componentTreeData}
                placeholder="选择组件"
                value={componentSearch}
                onChange={setComponentSearch}
                treeCheckable={true}
                showCheckedStrategy={TreeSelect.SHOW_CHILD}
                allowClear
                style={{ width: 200 }}
              />
            </div>
            
            {/* 新增token数筛选条件 */}
            <div>
              <span style={{ marginRight: '8px' }}>token数:</span>
              <Space>
                <Select
                  value={tokenCountSearch.operator}
                  onChange={value => setTokenCountSearch({...tokenCountSearch, operator: value})}
                  style={{ width: 100 }}
                >
                  <Option value="greater">大于</Option>
                  <Option value="less">小于</Option>
                  <Option value="between">区间</Option>
                </Select>
                <Input
                  type="number"
                  placeholder="token数"
                  value={tokenCountSearch.value1 === null ? undefined : tokenCountSearch.value1}
                  onChange={e => setTokenCountSearch({
                    ...tokenCountSearch, 
                    value1: e.target.value ? Number(e.target.value) : null
                  })}
                  style={{ width: 90 }}
                />
                {tokenCountSearch.operator === 'between' && (
                  <>
                    <span>-</span>
                    <Input
                      type="number"
                      placeholder="token数"
                      value={tokenCountSearch.value2 === null ? undefined : tokenCountSearch.value2}
                      onChange={e => setTokenCountSearch({
                        ...tokenCountSearch, 
                        value2: e.target.value ? Number(e.target.value) : null
                      })}
                      style={{ width: 90 }}
                    />
                  </>
                )}
              </Space>
            </div>
          </div>
          
          {/* 删除卡片领域搜索 */}
          
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button type="primary" onClick={handleSearch}>
              搜索
            </Button>
            <Button onClick={resetSearch}>重置</Button>
          </div>
        </div>
      </div>
      
      <Table
        columns={columns}
        dataSource={cards}
        rowKey="id"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          pageSizeOptions: ['10', '20', '50', '100', '200', '500']
        }}
        onChange={handleTableChange}
        scroll={{ x: 1500 }}
      />

      <Modal
        title="卡片DSL数据"
        open={jsonModalVisible}
        onCancel={() => setJsonModalVisible(false)}
        footer={null}
        width={800}
      >
        <div style={{ 
          backgroundColor: '#1e1e1e',
          padding: '12px',
          borderRadius: '4px',
          maxHeight: '500px',
          overflow: 'auto'
        }}>
          {currentJson && (
            <ReactJson
              src={currentJson}
              theme="monokai"
              displayDataTypes={false}
              collapsed={false}
              collapseStringsAfterLength={80}
              style={{ backgroundColor: 'transparent' }}
              enableClipboard={true}
              displayObjectSize={false}
              iconStyle="triangle"
              shouldCollapse={false}
              groupArraysAfterLength={100}
              onSelect={(select) => console.log('selected:', select)}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CardAnnotation;